import './processShim';
import React, { useContext } from 'react';
import ReactDOM from 'react-dom';
import { ErrorBoundary } from 'react-error-boundary';
import App from './App';
import { AuthContextProvider, AuthContext } from './context/AuthContext';
import 'normalize.css';
import { ViewportProvider } from './context/ViewPortContext';
import { QueryClientProvider, QueryClient } from 'react-query';
import { BrowserRouter } from 'react-router-dom';
import { StylesProvider } from '@material-ui/core/styles';
import generateClassName from './utils/jssSetup';
import { SuspensionProvider } from './context/SuspensionContext';
import { ThemeProvider } from './context/ThemeContext';
import ErrorMessage from './components/errorMessage/ErrorMessage.jsx';
import { ConversationsProvider } from './context/ConversationContext';
import { Toaster, toast } from 'react-hot-toast';
import './index.css';
// ✅ Use the upgraded provider + singleton from ONE place
import { SocketProvider } from './context/socket';
import ComposerProvider from './components/composer/ComposerProvider';
import { CallProvider } from './context/CallContext';
import GlobalCallModal from './components/globalCallModal/GlobalCallModal';
import CallOverlay from './components/callOverlay/CallOverlay';
import { PresenceProvider } from './context/PresenceContext';
import { LocalOpsLiveProvider } from './context/LocalOpsLiveContext';
import './mapboxWorkerDevShim';
import './devErrorLogger';
const queryClient = new QueryClient();

const Root = () => {
  const { player } = useContext(AuthContext);

  // Derive values safely from your player shape
  const token = player?.token || null;
  const playerId =
    player?.data?.player?._id ||
    player?.data?.player?.playerId ||
    player?._id ||
    player?.playerId ||
    null;

  return (
    <SuspensionProvider playerId={player?.data?.player?._id}>
      <QueryClientProvider client={queryClient}>
        {/* 🔐 Single auth-aware socket wrapper; no extra SocketContext.Provider needed */}
        <SocketProvider token={token} playerId={playerId}>
          <PresenceProvider>
            <LocalOpsLiveProvider>
            <ViewportProvider>
              <BrowserRouter>
                {/* 🔊 Global A/V state and UI (accept from any page) */}
                <CallProvider>
                  <StylesProvider generateClassName={generateClassName}>
                    <ThemeProvider playerToken={token}>
                      <ErrorBoundary FallbackComponent={ErrorMessage}>
                        <ConversationsProvider>
                          <ComposerProvider>
                            <App />
                            {/* Always-mounted global components */}
                            <GlobalCallModal />
                            <CallOverlay />
                          </ComposerProvider>
                        </ConversationsProvider>
                      </ErrorBoundary>
                    </ThemeProvider>
                  </StylesProvider>
                </CallProvider>
              </BrowserRouter>
            </ViewportProvider>
            </LocalOpsLiveProvider>
          </PresenceProvider>
        </SocketProvider>
      </QueryClientProvider>
    </SuspensionProvider>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <AuthContextProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 5000,
          style: { borderRadius: 12, background: '#111', color: '#fff' },
        }}
      />
      <Root />
    </AuthContextProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);

/* ---------------- Service Worker registration (unchanged, solid) ---------------- */
// index.js (or wherever you register the SW)
const SW_URL = '/sw.js';
const SW_CONTROLLER_RELOAD_KEY = 'jg:sw-controller-reload-v1';

if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      console.log('[SW] window loaded');
      const hadControllerOnLoad = !!navigator.serviceWorker.controller;

      // 1) Always register; browsers dedupe and return the same registration.
      const reg = await navigator.serviceWorker.register(SW_URL, {
        scope: '/',
      });
      console.log('[SW] registered:', reg.scope, reg);

      // 2) If a worker is already waiting (e.g., previous install step finished off-page), activate it now.
      if (reg.waiting) {
        console.log('[SW] waiting worker detected → sending SKIP_WAITING');
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // 3) Detect fresh updates while the app is open.
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        console.log('[SW] updatefound: installing=', installing);
        if (!installing) return;

        installing.addEventListener('statechange', () => {
          console.log('[SW] installing.state:', installing.state);

          // When a new worker reaches 'installed'
          if (installing.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // App is already controlled → this is an update.
              // Ask the new worker to skip waiting so it can activate immediately.
              console.log(
                '[SW] new worker installed while controlled → SKIP_WAITING',
              );
              reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
            } else {
              // First install on this page; the new worker will control after activation.
              console.log('[SW] first SW installed; awaiting activation/claim');
            }
          }
        });
      });

      // 4) When the active controlling worker changes, we know the new one took over.
      let didHandleControllerChange = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (didHandleControllerChange) return;
        didHandleControllerChange = true;
        console.log(
          '[SW] controllerchange → page is now controlled by the new SW',
        );

        if (!hadControllerOnLoad) return;
        try {
          if (window.sessionStorage.getItem(SW_CONTROLLER_RELOAD_KEY)) return;
          window.sessionStorage.setItem(
            SW_CONTROLLER_RELOAD_KEY,
            String(Date.now()),
          );
        } catch (err) {
          // If storage is blocked, still do the controller-change reload.
        }
        console.log('[SW] soft-reload after control transfer');
        window.location.reload();
      });

      navigator.serviceWorker.addEventListener('message', (e) => {
        if (e.data?.type === 'SW_ACTIVE') {
          console.log('[SW] active version:', e.data.version);
        }
      });

      // 5) Debug current control status
      if (navigator.serviceWorker.controller) {
        console.log(
          '[SW] page is already controlled by:',
          navigator.serviceWorker.controller,
        );
      } else {
        console.log(
          '[SW] page not yet controlled; will flip on controllerchange after activation/claim',
        );
      }
    } catch (err) {
      console.error('[SW] registration error:', err);
    }
  });
}

// const SW_URL = '/sw.js';

// if ('serviceWorker' in navigator) {
//   window.addEventListener('load', () => {
//     console.log('[SW] window loaded, checking for existing registration...');
//     navigator.serviceWorker.getRegistration(SW_URL).then((existingReg) => {
//       if (existingReg) {
//         console.log('[SW] already registered:', existingReg.scope, existingReg);
//       } else {
//         console.log('[SW] no existing registration, registering new SW...');
//         navigator.serviceWorker
//           .register(SW_URL, { scope: '/' })
//           .then((reg) => {
//             console.log('[SW] registered:', reg.scope, reg);

//             reg.addEventListener('updatefound', () => {
//               const installing = reg.installing;
//               console.log('[SW] update found:', installing);
//               if (!installing) return;
//               installing.addEventListener('statechange', () => {
//                 console.log('[SW] statechange:', installing.state);
//                 if (installing.state === 'installed') {
//                   if (navigator.serviceWorker.controller) {
//                     console.log('[SW] new update available, reloading...');
//                     window.location.reload(); // or prompt user to refresh
//                   } else {
//                     console.log('[SW] installed for the first time.');
//                   }
//                 }
//               });
//             });
//           })
//           .catch((err) => {
//             console.error('[SW] registration failed:', err);
//           });
//       }
//     });
//   });
// }
